import diffusion from 'diffusion';
import LiveScoreAdapter from '../livescoreAdapter';

class LiveScore extends LiveScoreAdapter {
  constructor() {
    super();
    this.didConnect = false;
    this.diffusionSession = null;
    this.subscribedTopics = {};
    this.genericSummaryHandler = this.genericSummaryHandler.bind(this);
    this.footballDetailsHandler = this.footballDetailsHandler.bind(this);
    this.tennisHandler = this.tennisHandler.bind(this);
    this.dataHandler = this.dataHandler.bind(this);
    this.prevValues = {};
  }

  static createTopic({ eventId, trackDetails }) {
    return [
      trackDetails ? `scoreboards/v1/${eventId}` : `scoreboards/v1/${eventId}/summary`
    ];
  }

  static getEventId(topic) {
    const parts = topic.split('/');

    return parts[2];
  }

  connect(params) {
    if (this.didConnect) {
      return;
    }
    this.didConnect = true;
    const errorHandler = (error) => {
      this.didConnect = false;
      this.fire('connect_error', error);
      this.resetSubscriptions();
    };
    diffusion.connect(params)
      .then((session) => {
        this.diffusionSession = session;
        this.diffusionSession.on({
          disconnect: (reason) => {
            this.fire('connect_warning', reason);
          },
          reconnect: () => {
            this.fire('reconnect', this.diffusionSession.isConnected());
          },
          error: errorHandler,
          close: (reason) => {
            this.didConnect = false;
            this.fire('disconnect', reason);
            this.resetSubscriptions();
          }
        });
        this.fire('connect', this.diffusionSession.isConnected());
        this.forwardSubscriptions();
      }, errorHandler);
  }

  disconnect() {
    this.didConnect = false;
    if (this.diffusionSession && !this.diffusionSession.isClosed()) {
      this.diffusionSession.close();
    }
  }

  subscribe(sportEvent) {
    const topics = LiveScore.createTopic(sportEvent);

    topics.forEach(topic => {
      if (this.subscribedTopics[topic] !== true) {
        this.subscribedTopics[topic] = false;
      }
    });
    this.subscribedEvents.push(sportEvent);
    this.forwardSubscriptions();
  }

  unsubscribe(sportEvent) {
    const topics = LiveScore.createTopic(sportEvent);
    topics.forEach(topic => {
      const eventId = LiveScore.getEventId(topic);

      if (this.diffusionSession) {
        this.diffusionSession.unsubscribe(topic);
      }
      this.subscribedTopics[topic] = null;
      this.fire('unsubscribe', { eventId, topic });
    });
    const eventNotEquals = e1 => e2 => e1.sport !== e2.sport || e1.eventId !== e2.eventId;
    this.subscribedEvents = this.subscribedEvents.filter(eventNotEquals(sportEvent));
  }

  static durationWithTimePrecision(eventInfo, duration) {
    if (duration && eventInfo && eventInfo.timePrecision > 1) {
      return Math.floor(duration / eventInfo.timePrecision) * eventInfo.timePrecision;
    }
    return duration;
  }

  genericSummaryHandler(topic, value, result) {
    const {
      score: { home, away } = {},
      clock: {
        currentTime: { duration, period } = {}
      } = {}
    } = value;
    const eventInfo = this.subscribedEvents.find(e => e.eventId === LiveScore.getEventId(topic));
    const roundedDuration = LiveScore.durationWithTimePrecision(eventInfo, duration);
    const prevValue = this.prevValues[topic] || {};
    const isNewDuration = prevValue.duration !== roundedDuration;
    const isNewScore = prevValue.scoreHome !== home || prevValue.scoreAway !== away;
    const isNewPeriod = prevValue.period !== period;

    if (isNewDuration) {
      this.fire('data', Object.assign({}, result, {
        type: 'time',
        seconds: roundedDuration,
        time: LiveScore.formatTime(roundedDuration)
      }));
    }
    if (isNewScore) {
      this.fire('data', Object.assign({}, result, {
        type: 'score',
        scoreA: `${home}`,
        scoreB: `${away}`
      }));
    }
    if (isNewPeriod) {
      this.fire('data', Object.assign({}, result, {
        type: 'currentPeriod',
        period: LiveScore.formatPeriod(period)
      }));
    }

    this.prevValues[topic] = Object.assign({}, this.prevValues[topic], {
      duration: roundedDuration,
      scoreHome: home,
      scoreAway: away,
      period
    });
  }

  footballStatsChecksum(topic) {
    const eventInfo = this.subscribedEvents.find(e => e.eventId === LiveScore.getEventId(topic));
    return (acc, next) => {
      const [field] = next;
      let [, { home: homeValue, away: awayValue }] = next;
      if (field === 'possessionTime') {
        homeValue = LiveScore.durationWithTimePrecision(eventInfo, homeValue);
        awayValue = LiveScore.durationWithTimePrecision(eventInfo, awayValue);
      }
      return acc + homeValue + awayValue;
    };
  }

  footballDetailsHandler(topic, value, result) {
    this.genericSummaryHandler(topic, value, result);

    const {
      cards = [],
      goals = [],
      stats: {
        TOTAL: totalStats = {}
      } = {}
    } = value;
    const {
      cardsCount: prevCardsCount = 0,
      goalsCount: prevGoalsCount = 0,
      totalStatsChecksum: prevStatsChecksum = 0
    } = this.prevValues[topic];
    const isNewCard = prevCardsCount !== cards.length;
    const isNewGoal = prevGoalsCount !== goals.length;
    const totalStatsChecksum = Object.entries(totalStats)
      .reduce(this.footballStatsChecksum(topic), 0);
    const isNewStats = prevStatsChecksum !== totalStatsChecksum;

    const withFormattedFields = goalOrCard => Object.assign({}, goalOrCard, {
      timeFormatted: LiveScore.formatTime(goalOrCard.time.duration),
      periodFormatted: LiveScore.formatPeriod(goalOrCard.time.period)
    });
    if (isNewCard) {
      this.fire('data', Object.assign({}, result, {
        type: 'cards',
        cards: cards.map(withFormattedFields)
      }));
    }
    if (isNewGoal) {
      this.fire('data', Object.assign({}, result, {
        type: 'goals',
        goals: goals.map(withFormattedFields)
      }));
    }
    if (isNewStats) {
      this.fire('data', Object.assign({}, result, {
        type: 'stats',
        total: totalStats
      }));
    }

    this.prevValues[topic] = Object.assign({}, this.prevValues[topic], {
      cardsCount: cards.length,
      goalsCount: goals.length,
      totalStatsChecksum
    });
  }

  tennisHandler(topic, value, result) {
    const defaultResult = { p1: '', p2: '' };
    const {
      score: { match = defaultResult, currentSet = defaultResult } = {}
    } = value;

    if (!match.p1 && !match.p2) {
      return;
    }

    this.fire('data', Object.assign({}, result, {
      type: 'score',
      scoreA: `${match.p1}`,
      scoreB: `${match.p2}`,
      scoreLabel: 'S:',
      subScoreA: `${currentSet.p1}`,
      subScoreB: `${currentSet.p2}`,
      subScoreLabel: 'G:'
    }));
  }

  dataHandler(topic, specification, data) {
    const value = data.get();
    const result = {
      type: 'raw_data',
      eventId: value.id,
      topic
    };

    const sportSpecificHandlers = {
      FOOTBALL: this.footballDetailsHandler,
      TENNIS: this.tennisHandler
    };
    const selectedHandler = sportSpecificHandlers[value.type] || this.genericSummaryHandler;
    selectedHandler(topic, value, result);

    this.fire('raw_data', Object.assign({}, result, { value }));
  }

  forwardSubscriptions() {
    if (this.diffusionSession && this.diffusionSession.isConnected()) {
      Object.keys(this.subscribedTopics)
        .filter(topic => this.subscribedTopics[topic] === false)
        .forEach(topic => {
          const eventId = LiveScore.getEventId(topic);

          this.diffusionSession.addStream(topic, diffusion.datatypes.json())
            .on('value', this.dataHandler);
          this.diffusionSession.select(topic).then(() => {
            this.subscribedTopics[topic] = true;
            this.fire('subscribe', { eventId, topic });
          });
        });
    }
  }

  resetSubscriptions() {
    this.prevValues = {};
    Object.keys(this.subscribedTopics)
      .filter(topic => this.subscribedTopics[topic] === true)
      .forEach(topic => {
        this.subscribedTopics[topic] = false;
      });
  }
}

export default LiveScore;
